import { createEffect, createMemo, Show } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { FileDown, Mail as MailIcon, Package, PenSquare, Search } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { showToast } from "@/components/ui/toast";
import { QueryErrorState } from "@/components/ui/query-error-state";
import type { MailSearch } from "@/features/mail/route-search";
import {
  useMailAccounts,
  useMailFolders,
  useMailMessage,
  useMailMessages,
  useMoveMessage,
  useSetFlags,
  useSyncAccount,
} from "../hooks";
import { IMAP_FLAG_FLAGGED, IMAP_FLAG_SEEN, type MessageSummary } from "../api";
import { downloadFolderCsv, downloadFolderMbox, downloadMessageEml } from "../export";
import { EXPORT_DELIVERY } from "@fleet/plugins/export";
import { useKernelService } from "@fleet/kernel";
import { useInboxZero } from "../gamification";
import { FolderRail } from "./folder-rail";
import { MessageList } from "./message-list";
import { MessageReader } from "./message-reader";

const DEFAULT_FOLDER = "INBOX";

export function Mailbox() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_app/mail" }) as () => MailSearch;

  const accountsQuery = useMailAccounts();
  const syncMutation = useSyncAccount();
  const flagsMutation = useSetFlags();
  const moveMutation = useMoveMessage();
  const delivery = useKernelService(EXPORT_DELIVERY);

  // P23 / CC-60 · I2: `.data` only on `isSuccess`. `?? []` on missing `data`
  // converts "could not read" into "you have no accounts", and additionally
  // reading would suspend the entire route while the request is in flight.
  const accounts = createMemo(() => (accountsQuery.isSuccess ? (accountsQuery.data ?? []) : []));

  const activeAccountId = createMemo(() => {
    const list = accounts();
    const requested = search().acct;
    if (requested && list.some((a) => a.id === requested)) {
      return requested;
    }
    return list[0]?.id;
  });

  const activeFolder = createMemo(() => search().folder ?? DEFAULT_FOLDER);
  const activeUid = createMemo(() => search().uid);

  const foldersQuery = useMailFolders(() => activeAccountId());
  // P23 / CC-60 · I2: each `.data` passes through its `isSuccess` guard. They are
  // the only fields on the solid-query proxy that suspend.
  const folders = createMemo(() => (foldersQuery.isSuccess ? (foldersQuery.data ?? []) : []));
  // Light gamification: reaching zero unread in the INBOX records the day and
  // builds a streak (client-side, per account).
  const inboxZero = useInboxZero(
    () => activeAccountId(),
    () => (foldersQuery.isSuccess ? foldersQuery.data : undefined),
  );
  const messagesQuery = useMailMessages(
    () => activeAccountId(),
    () => activeFolder(),
  );
  const messages = createMemo(() => (messagesQuery.isSuccess ? messagesQuery.data : undefined));
  const messageQuery = useMailMessage(
    () => activeAccountId(),
    () => activeFolder(),
    () => activeUid(),
  );
  const activeMessage = createMemo(() => (messageQuery.isSuccess ? messageQuery.data : undefined));

  // Normalize the URL: once accounts resolve, pin the active account id so the
  // reader/list keys are stable and the view is shareable.
  createEffect(() => {
    const resolved = activeAccountId();
    if (resolved && search().acct !== resolved) {
      void navigate({
        to: "/mail",
        search: (prev) => ({ ...prev, acct: resolved }),
        replace: true,
      });
    }
  });

  const patchSearch = (patch: Partial<MailSearch>) => {
    void navigate({ to: "/mail", search: (prev) => ({ ...prev, ...patch }), replace: true });
  };

  const openMessage = (m: MessageSummary) => {
    patchSearch({ uid: m.uid });
    // Mark read on open (optimistic).
    const id = activeAccountId();
    if (id && !m.seen) {
      flagsMutation.mutate({
        accountId: id,
        uid: m.uid,
        folder: activeFolder(),
        body: { folder: activeFolder(), add: [IMAP_FLAG_SEEN] },
      });
    }
  };

  const toggleFlag = (m: { uid: number; flagged: boolean }) => {
    const id = activeAccountId();
    if (!id) {
      return;
    }
    flagsMutation.mutate({
      accountId: id,
      uid: m.uid,
      folder: activeFolder(),
      body: m.flagged
        ? { folder: activeFolder(), remove: [IMAP_FLAG_FLAGGED] }
        : { folder: activeFolder(), add: [IMAP_FLAG_FLAGGED] },
    });
  };

  const toggleSeenActive = () => {
    const id = activeAccountId();
    const detail = activeMessage();
    if (!id || !detail) {
      return;
    }
    flagsMutation.mutate({
      accountId: id,
      uid: detail.uid,
      folder: activeFolder(),
      body: detail.seen
        ? { folder: activeFolder(), remove: [IMAP_FLAG_SEEN] }
        : { folder: activeFolder(), add: [IMAP_FLAG_SEEN] },
    });
  };

  const moveActive = (dest: string) => {
    const id = activeAccountId();
    const detail = activeMessage();
    if (!id || !detail) {
      return;
    }
    moveMutation.mutate(
      { accountId: id, uid: detail.uid, body: { folder: activeFolder(), dest } },
      { onSuccess: () => patchSearch({ uid: undefined }) },
    );
  };

  // ── K1 export ──────────────────────────────────────────────────────────────
  const exportEmlActive = () => {
    const id = activeAccountId();
    const detail = activeMessage();
    if (!id || !detail) {
      return;
    }
    void downloadMessageEml(id, detail.uid, activeFolder()).catch(() => {
      showToast(t(msg`Could not export the message.`), "error");
    });
  };

  const exportFolderMboxNow = () => {
    const id = activeAccountId();
    if (!id) {
      return;
    }
    void downloadFolderMbox(id, activeFolder()).catch(() => {
      showToast(t(msg`Could not export the folder.`), "error");
    });
  };

  const exportFolderCsvNow = () => {
    const id = activeAccountId();
    const list = messages();
    if (!id || !list || list.length === 0) {
      showToast(t(msg`Nothing to export in this folder.`), "info");
      return;
    }
    const target = delivery();
    if (!target) {
      return;
    }
    const email = accounts().find((a) => a.id === id)?.email;
    downloadFolderCsv(list, activeFolder(), email, target);
  };

  const gotoCompose = (mode: "reply" | "reply-all" | "forward") => {
    const id = activeAccountId();
    const detail = activeMessage();
    if (!id || !detail) {
      return;
    }
    void navigate({
      to: "/mail/compose",
      search: { acct: id, folder: activeFolder(), uid: detail.uid, mode },
    });
  };

  return (
    <div class="flex min-h-0 flex-1" data-testid="mailbox">
      {/* P23 / CC-60 · I3: "no response" ≠ "nothing here". With the mail
          API down (5xx) this screen told a user who DOES have a connected mailbox
          to "Connect your mailbox" — lying about the state of their own product
          and inviting them to reconnect an existing account.
          NOTE the local-first nuance of this repo: a NETWORK failure (fetch without
          `status`) does NOT reach here — `mailMirror` treats it as "offline" and serves
          the IndexedDB mirror by design. What reaches here is an error with
          an HTTP status, which is precisely the case that lied.
          The content block is WRAPPED, not chained: on error
          `isLoading` is `false`, so a `when={!isLoading && !isError}` with
          `fallback={skeleton}` would paint error + skeleton simultaneously, forever. */}
      <Show when={accountsQuery.isError}>
        <div class="flex flex-1 items-center justify-center p-8">
          <QueryErrorState
            testId="mailbox-error"
            title={t(msg`Couldn't load your mailboxes`)}
            description={t(
              msg`We couldn't reach the mail server. Your accounts are still there — this is a connection problem.`,
            )}
            onRetry={() => void accountsQuery.refetch()}
          />
        </div>
      </Show>
      <Show when={!accountsQuery.isError}>
        <Show
          when={!accountsQuery.isLoading}
          fallback={<div class="flex-1" data-testid="mail-accounts-loading" />}
        >
          <Show
            when={accounts().length > 0}
            fallback={
              <div
                class="flex flex-1 items-center justify-center p-8"
                data-testid="mail-accounts-empty"
              >
                <EmptyState
                  icon={MailIcon}
                  title={t(msg`Connect your mailbox`)}
                  description={t(
                    msg`Add an IMAP/SMTP account to start reading and sending mail in Cartero.`,
                  )}
                  action={
                    <Button
                      onClick={() => navigate({ to: "/mail/settings" })}
                      data-testid="mail-connect-cta"
                    >
                      {t(msg`Connect an account`)}
                    </Button>
                  }
                />
              </div>
            }
          >
            <FolderRail
              accounts={accounts()}
              activeAccountId={activeAccountId()}
              onAccountChange={(id) =>
                patchSearch({ acct: id, folder: DEFAULT_FOLDER, uid: undefined })
              }
              folders={folders()}
              activeFolder={activeFolder()}
              onFolderChange={(name) => patchSearch({ folder: name, uid: undefined })}
              onSync={() => {
                const id = activeAccountId();
                if (id) {
                  syncMutation.mutate(id);
                }
              }}
              syncing={syncMutation.isPending}
              onOpenSettings={() => navigate({ to: "/mail/settings" })}
            />

            <div class="flex min-h-0 flex-1 flex-col">
              <header class="flex items-center gap-2 border-b border-border/40 px-4 py-2.5">
                <h2 class="font-serif text-lg font-semibold tracking-tight text-foreground">
                  {activeFolder()}
                </h2>
                <div class="ml-auto flex items-center gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    class="gap-1.5"
                    onClick={exportFolderCsvNow}
                    aria-label={t(msg`Export folder as CSV`)}
                    data-testid="mail-export-csv"
                  >
                    <FileDown class="h-4 w-4" />
                    <span class="hidden lg:inline">{t(msg`CSV`)}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    class="gap-1.5"
                    onClick={exportFolderMboxNow}
                    aria-label={t(msg`Export folder as mbox`)}
                    data-testid="mail-export-mbox"
                  >
                    <Package class="h-4 w-4" />
                    <span class="hidden lg:inline">{t(msg`mbox`)}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    class="gap-1.5"
                    onClick={() =>
                      navigate({ to: "/mail/search", search: { acct: activeAccountId() } })
                    }
                    data-testid="mail-search-open"
                  >
                    <Search class="h-4 w-4" />
                    <span class="hidden sm:inline">{t(msg`Search`)}</span>
                  </Button>
                  <Button
                    size="sm"
                    class="gap-1.5"
                    onClick={() =>
                      navigate({ to: "/mail/compose", search: { acct: activeAccountId() } })
                    }
                    data-testid="mail-compose-open"
                  >
                    <PenSquare class="h-4 w-4" />
                    <span class="hidden sm:inline">{t(msg`Compose`)}</span>
                  </Button>
                </div>
              </header>

              <div class="flex min-h-0 flex-1">
                <div
                  class={
                    activeUid() === undefined
                      ? "flex min-h-0 flex-1 flex-col"
                      : "hidden min-h-0 w-full max-w-md flex-col border-r border-border/40 lg:flex"
                  }
                >
                  <MessageList
                    messages={messages()}
                    loading={messagesQuery.isLoading}
                    error={messagesQuery.isError}
                    onRetry={() => void messagesQuery.refetch()}
                    activeUid={activeUid()}
                    onSelect={openMessage}
                    onToggleFlag={toggleFlag}
                    inboxZeroStreak={activeFolder() === DEFAULT_FOLDER ? inboxZero.streak() : 0}
                  />
                </div>

                <Show when={activeUid() !== undefined}>
                  <MessageReader
                    detail={activeMessage()}
                    loading={messageQuery.isLoading}
                    folders={folders()}
                    activeFolder={activeFolder()}
                    onReply={() => gotoCompose("reply")}
                    onReplyAll={() => gotoCompose("reply-all")}
                    onForward={() => gotoCompose("forward")}
                    onToggleFlag={() => {
                      const d = activeMessage();
                      if (d) {
                        toggleFlag(d);
                      }
                    }}
                    onToggleSeen={toggleSeenActive}
                    onMove={moveActive}
                    onExportEml={exportEmlActive}
                    onClose={() => patchSearch({ uid: undefined })}
                  />
                </Show>
              </div>
            </div>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
